import {
  clamp01,
} from "../core/math.js";
import { lerp } from "../core/math.js";

function roundUpToIncrement(value, increment) {
  if (!Number.isFinite(value) || !Number.isFinite(increment) || increment <= 0) return value;
  return Math.ceil(value / increment) * increment;
}

export function rawValueExtent(data, key) {
  let min = Infinity;
  let max = -Infinity;
  data.forEach((point) => {
    const value = point[key];
    if (!Number.isFinite(value)) return;
    if (value < min) min = value;
    if (value > max) max = value;
  });
  return min === Infinity ? null : { min, max };
}

function finiteValuesForKey(data, key) {
  const values = [];
  data.forEach((point) => {
    const value = point[key];
    if (Number.isFinite(value)) values.push(value);
  });
  return values;
}

function quantile(sortedValues, ratio) {
  if (!sortedValues.length) return null;
  const index = (sortedValues.length - 1) * ratio;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  return lerp(sortedValues[lower], sortedValues[upper], index - lower);
}

function robustValueExtent(data, key, meta = null) {
  const values = finiteValuesForKey(data, key);
  if (!values.length) return null;
  let min = Infinity;
  let max = -Infinity;
  values.forEach((value) => {
    if (value < min) min = value;
    if (value > max) max = value;
  });
  if (values.length < 12 || min === max) return { min, max };

  const sorted = [...values].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  if (!Number.isFinite(q1) || !Number.isFinite(q3)) return { min, max };

  const median = quantile(sorted, 0.5) ?? 0;
  const metaSpan = finiteMetaSpan(meta);
  const spread = Math.max(
    q3 - q1,
    metaSpan ? metaSpan * 0.04 : 0,
    Math.abs(median) * 0.03,
    1,
  );
  const lowerFence = q1 - spread * 4;
  const upperFence = q3 + spread * 4;
  const lowOutliers = sorted.findIndex((value) => value >= lowerFence);
  const firstHighIndex = sorted.findIndex((value) => value > upperFence);
  const lowCount = lowOutliers === -1 ? sorted.length : lowOutliers;
  const highCount = firstHighIndex === -1 ? 0 : sorted.length - firstHighIndex;
  const isolatedLimit = Math.min(60, Math.max(3, Math.ceil(sorted.length * 0.02)));
  const extremeGap = Math.max(
    spread * 6,
    metaSpan ? metaSpan * 1.25 : 0,
    Math.abs(median) * 0.35,
    10,
  );

  for (let count = 1; count <= isolatedLimit && count < sorted.length - 2; count += 1) {
    const splitIndex = sorted.length - count;
    if (sorted[splitIndex] - sorted[splitIndex - 1] >= extremeGap) {
      max = sorted[splitIndex - 1];
    }
  }

  for (let count = 1; count <= isolatedLimit && count < sorted.length - 2; count += 1) {
    if (sorted[count] - sorted[count - 1] >= extremeGap) {
      min = sorted[count];
    }
  }

  if (lowCount > 0 && lowCount <= isolatedLimit && sorted.length - lowCount >= 3) {
    min = sorted[lowCount];
  }
  if (highCount > 0 && highCount <= isolatedLimit && sorted.length - highCount >= 3) {
    max = sorted[sorted.length - highCount - 1];
  }

  return { min, max };
}

export function displayValueExtent(data, key, meta = null) {
  return robustValueExtent(data, key, meta);
}

function finiteMetaSpan(meta) {
  if (!Number.isFinite(meta?.min) || !Number.isFinite(meta?.max)) return null;
  const span = meta.max - meta.min;
  return span > 0 ? span : null;
}

function expandRangeToMinimumSpan(min, max, minSpan, meta) {
  const span = max - min;
  if (!Number.isFinite(minSpan) || minSpan <= 0 || span >= minSpan) return { min, max };

  let nextMin = min;
  let nextMax = max;
  const center = (min + max) / 2;
  nextMin = center - minSpan / 2;
  nextMax = center + minSpan / 2;

  const metaSpan = finiteMetaSpan(meta);
  if (metaSpan && minSpan <= metaSpan) {
    if (nextMin < meta.min) {
      nextMax += meta.min - nextMin;
      nextMin = meta.min;
    }
    if (nextMax > meta.max) {
      nextMin -= nextMax - meta.max;
      nextMax = meta.max;
    }
  }

  return { min: nextMin, max: nextMax };
}

export function valueRange(data, key, meta, independentScale) {
  if (!independentScale) return { min: meta.min, max: meta.max };
  const rawExtent = rawValueExtent(data, key);
  if (!rawExtent) return { min: meta.min, max: meta.max };
  if (key === "buss") return busVoltageAxisRange(rawExtent, meta);
  const extent = displayValueExtent(data, key, meta) ?? rawExtent;
  let { min, max } = extent;
  const spread = max - min;
  const padding = spread > 0 ? spread * 0.06 : Math.max(Math.abs(max) * 0.06, 1);
  min -= padding;
  max += padding;
  const metaSpan = finiteMetaSpan(meta);
  const minimumSpan = metaSpan
    ? Math.max(metaSpan * 0.18, 1)
    : Math.max(Math.abs(max) * 0.12, 1);
  ({ min, max } = expandRangeToMinimumSpan(min, max, minimumSpan, meta));
  if (min === max) max = min + 1;
  return { min, max };
}

export function combinedValueRange(data, keys) {
  let min = Infinity;
  let max = -Infinity;
  keys.forEach((key) => {
    const extent = displayValueExtent(data, key);
    if (!extent) return;
    if (extent.min < min) min = extent.min;
    if (extent.max > max) max = extent.max;
  });
  if (min === Infinity) return { min: 0, max: 1 };
  const spread = max - min;
  const padding = spread > 0 ? spread * 0.08 : Math.max(Math.abs(max) * 0.08, 1);
  min -= padding;
  max += padding;
  if (min === max) max = min + 1;
  return { min, max };
}

export function axisRangeForData(data, keys, baseRange, options: Record<string, any> = {}) {
  const expandLower = options.expandLower ?? true;
  const expandUpper = options.expandUpper ?? true;
  const lowerIncrement = options.lowerIncrement ?? 50;
  const upperIncrement = options.upperIncrement ?? 25;
  const lowerHeadroom = options.lowerHeadroom ?? 0;
  const upperHeadroom = options.upperHeadroom ?? 0;
  const lowerFloor = Number.isFinite(options.lowerFloor) ? options.lowerFloor : null;
  const upperCeiling = Number.isFinite(options.upperCeiling) ? options.upperCeiling : null;
  let min = Infinity;
  let max = -Infinity;

  (data ?? []).forEach((point) => {
    keys.forEach((key) => {
      const value = point[key];
      if (!Number.isFinite(value)) return;
      if (value < min) min = value;
      if (value > max) max = value;
    });
  });

  if (min === Infinity) return baseRange;
  if (options.fit) {
    let lo = Math.floor((min - lowerHeadroom) / lowerIncrement) * lowerIncrement;
    let hi = Math.ceil((max + upperHeadroom) / upperIncrement) * upperIncrement;
    if (lowerFloor !== null) lo = Math.max(lo, lowerFloor);
    const minSpan = options.minSpan ?? lowerIncrement * 2;
    if (hi - lo < minSpan) hi = lo + minSpan;
    if (upperCeiling !== null && hi > upperCeiling) {
      hi = upperCeiling;
      if (hi - lo < minSpan) lo = hi - minSpan;
      if (lowerFloor !== null) lo = Math.max(lo, lowerFloor);
    }
    return { ...baseRange, min: lo, max: hi };
  }
  const nextRange = { ...baseRange };
  if (expandLower && min < baseRange.min) {
    nextRange.min = Math.floor((min - lowerHeadroom) / lowerIncrement) * lowerIncrement;
    if (lowerFloor !== null) nextRange.min = Math.max(nextRange.min, lowerFloor);
  }
  if (expandUpper && max > baseRange.max) {
    nextRange.max = Math.ceil((max + upperHeadroom) / upperIncrement) * upperIncrement;
    if (upperCeiling !== null) nextRange.max = Math.min(nextRange.max, upperCeiling);
  }
  return nextRange;
}

export function formatAxisLabel(value) {
  return Math.abs(value) >= 100 ? String(Math.round(value)) : value.toFixed(1);
}

export function axisTickForValue(value, range) {
  return {
    label: formatAxisLabel(value),
    ratio: clamp01((range.max - value) / Math.max(range.max - range.min, 1)),
  };
}

export function yLabelsForRange(range) {
  if (range?.label === "CHT (F)" && range.min === 175 && range.max === 600) {
    return [600, 500, 400, 300, 175].map((value) => axisTickForValue(value, range));
  }
  if (range?.label === "EGT (F)") {
    return Array.from({ length: 5 }, (_, index) => {
      const ratio = index / 4;
      const value = index === 0 || index === 4
        ? lerp(range.max, range.min, ratio)
        : roundUpToIncrement(lerp(range.max, range.min, ratio), 25);
      return axisTickForValue(value, range);
    });
  }
  return Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    return {
      label: formatAxisLabel(lerp(range.max, range.min, ratio)),
      ratio,
    };
  });
}

export function busVoltageAxisRange(extent, meta) {
  if (!extent) return { min: meta.min, max: meta.max };
  if (extent.max > 18) return { min: 20, max: 32 };
  return { min: meta.min, max: meta.max };
}

export function singleSeriesAxisRange(data, key, meta) {
  const rawExtent = rawValueExtent(data, key);
  if (key === "buss") return busVoltageAxisRange(rawExtent, meta);
  if (key !== "altitude") {
    const extent = displayValueExtent(data, key, meta) ?? rawExtent;
    if (!extent) return { min: meta.min, max: meta.max };
    const metaMin = Number.isFinite(meta.min) ? meta.min : extent.min;
    const metaMax = Number.isFinite(meta.max) ? meta.max : extent.max;
    if (extent.min >= metaMin && extent.max <= metaMax) return { min: metaMin, max: metaMax };

    const dataSpan = Math.max(extent.max - extent.min, 1);
    const metaSpan = Math.max(metaMax - metaMin, 1);
    const padding = Math.max(dataSpan * 0.08, metaSpan * 0.04, 1);
    return {
      min: extent.min < metaMin ? extent.min - padding : metaMin,
      max: extent.max > metaMax ? extent.max + padding : metaMax,
    };
  }
  const extent = rawExtent;
  if (!extent) return { min: meta.min, max: meta.max };
  let { min, max } = extent;
  const span = Math.max(max - min, 1);
  const padding = Math.max(100, span * 0.12);
  min = Math.max(0, min - padding);
  max += padding;
  if (max - min < 500) {
    const center = (min + max) / 2;
    min = Math.max(0, center - 250);
    max = center + 250;
  }
  return { min, max };
}
